import { useCallback, useMemo } from 'react';

/**
 * Hook for managing and executing actions across different features.
 * Provides a centralized way to register and execute actions for various features.
 *
 * @returns {Object} Object containing methods to register and execute actions
 *
 * @example
 * // In a component
 * const MyComponent = () => {
 *   const { registerFeature, makeAction } = useActions();
 *
 *   // Register a feature with actions
 *   useEffect(() => {
 *     const createUser = ({ data, setStepComponent }) => {
 *       // Implementation for creating a user
 *       setStepComponent({ type: 'loading', message: 'Creating user...' });
 *       // Async logic here...
 *     };
 *
 *     registerFeature('users', {
 *       create: createUser,
 *       delete: deleteUserAction,
 *     });
 *   }, [registerFeature]);
 *
 *   // Execute an action
 *   const handleCreateUser = (userData) => {
 *     makeAction({
 *       actionData: {
 *         target: 'users',
 *         view: {
 *           type: 'create',
 *           data: userData
 *         }
 *       },
 *       setStepComponent: (step) => setCurrentStep(step),
 *       getCancelSequence: () => cancelFn
 *     });
 *   };
 *
 *   return <button onClick={() => handleCreateUser({ name: 'John' })}>Create User</button>;
 * };
 */
const useActions = () => {
  // Define the features and their actions
  const features = useMemo(() => {
    // Features can be registered here or passed as props
    return {};
  }, []);

  /**
   * Registers a new feature with its associated actions
   *
   * @param {string} featureName - Name of the feature to register
   * @param {Object} actions - Object containing action functions for the feature
   */
  const registerFeature = useCallback((featureName, actions) => {
    features[featureName] = {
      actions
    };
  }, [features]);

  /**
   * Executes an action based on the provided action data
   *
   * @param {Object} options - Options for making the action
   * @param {Object} options.actionData - Data describing which action to execute
   * @param {string} options.actionData.target - The target feature
   * @param {Object} options.actionData.view - The view containing type and data
   * @param {Function} options.setStepComponent - Function to set the current step component
   * @param {Function} options.getCancelSequence - Function to get the cancel sequence
   */
  const makeAction = useCallback(_ref => {
    var _Object$keys, _features$target, _features$target$acti;
    let {
      actionData,
      setStepComponent,
      getCancelSequence
    } = _ref;
    if (!actionData.target || !((_Object$keys = Object.keys(actionData.view)) !== null && _Object$keys !== void 0 && _Object$keys.length)) return;
    const {
      target,
      view
    } = actionData;
    const action = (features === null || features === void 0 ? void 0 : (_features$target = features[target]) === null || _features$target === void 0 ? void 0 : (_features$target$acti = _features$target.actions) === null || _features$target$acti === void 0 ? void 0 : _features$target$acti[view.type]) || null;
    if (action) {
      action({
        data: view.data,
        setStepComponent,
        getCancelSequence
      });
    }
  }, [features]);
  return {
    makeAction,
    registerFeature
  };
};
export default useActions;